import { APP_ROUTES } from "../constants";
import { sendGA4Data } from "../analytics/events";
import { CONFIG } from './config';
import { events } from "../constants"

export const PAGE_PATHS = {
  apic: '/pages/solutions/api-central',
};
const LOCALE_PREFIX_REGEX = /^\/[a-z]{2}(?:-[a-z]{2})?(?=\/|$)/i;
const ABSOLUTE_HTTP_URL_REGEX = /^https?:\/\//i;
const trimTrailingSlashes = (value = '') => value.replace(/\/+$/, '');
const normalizePath = (value = '/') => `${value || '/'}`.replace(/\/{2,}/g, '/');
const normalizeRoutePath = (value = '/') => trimTrailingSlashes(normalizePath(value)) || '/';

const isShellOwnedRoutePath = (pathname = '/') => {
  const candidate = normalizeRoutePath(stripLocalePrefix(toShellRelativePath(pathname)));

  return Object.keys(APP_ROUTES).some((base) => {
    const routeBase = normalizeRoutePath(base);

    if (routeBase === '/myapps') {
      return CONFIG.ENABLE_HEADLESS_MYAPPS
        && (candidate === routeBase || candidate.startsWith(`${routeBase}/`));
    }

    return routeBase === '/'
      ? candidate === '/'
      : candidate === routeBase || candidate.startsWith(`${routeBase}/`);
  });
};

// Maps a URL pathname to a seo key in the translation files.
// Keys match translation.json seo object: 'home', 'listing', 'pages/ai',
// 'solutions/<attributeId>', 'industry/<attributeId>', etc.
const getSeoKeyForPath = (pathname) => {
  // Strip shell base path first (/marketplace/cmp/v1/static/cmp-spa), then locale prefix
  const stripped = toShellRelativePath(pathname).replace(LOCALE_PREFIX_REGEX, '') || '/';
  if (stripped === '/' || stripped.startsWith('/home')) return 'home';
  if (stripped.startsWith('/listing')) return 'listing';
  if (stripped.startsWith('/pages/ai')) return 'pages/ai';
  if (stripped.startsWith('/pages/why-marketplace')) return 'pages/why-marketplace';
  if (stripped.startsWith(PAGE_PATHS.apic)) return PAGE_PATHS.apic.slice(1);
  // solutions: /pages/solutions?attr=<id> or /en-US/pages/solutions?attr=<id>
  const solutionAttr = new URLSearchParams(window.location.search).get('attr');
  if (stripped.startsWith('/pages/solutions') && solutionAttr) return `solutions/${solutionAttr}`;
  if (stripped.startsWith('/pages/industry') && solutionAttr) return `industry/${solutionAttr}`;
  return null;
};

/**
 * Updates <title>, meta description, og:description, twitter:description,
 * og:url and <link rel="canonical"> from i18n translation seo keys.
 * Call on every SPA navigation from AppShell handleLocationChange.
 */
const LOCALE_REGEX = /\/([a-z]{2}-[A-Z]{2}(?:\([A-Z]+\))?)(?=\/|$)/;

const setMetaContent = (selector, value) => {
  const el = document.querySelector(selector);
  if (el) el.setAttribute('content', value);
};

const updateCanonical = (locale, pathname, search) => {
  const base = (getStoreBaseByLocale(locale) || process.env.REACT_APP_SHELL_URL || 'https://apps.adp.com').replace(/\/+$/, '');
  const canonicalUrl = base + pathname + search;
  const canonicalEl = document.querySelector('link[rel="canonical"]');
  if (canonicalEl) canonicalEl.setAttribute('href', canonicalUrl);
  setMetaContent('meta[property="og:url"]', canonicalUrl);
};

const updateSeoText = (i18nInstance, seoKey) => {
  const title = i18nInstance.t(`seo.${seoKey}.title`);
  const description = i18nInstance.t(`seo.${seoKey}.description`);
  if (title && !title.startsWith('seo.')) {
    document.title = title;
    setMetaContent('meta[property="og:title"]', title);
    setMetaContent('meta[property="twitter:title"]', title);
  }
  if (description && !description.startsWith('seo.')) {
    setMetaContent('meta[name="description"]', description);
    setMetaContent('meta[property="og:description"]', description);
    setMetaContent('meta[property="twitter:description"]', description);
  }
};

export const updatePageMeta = (i18nInstance) => {
  if (!i18nInstance) return;
  const { pathname, search } = window.location;
  const localeMatch = LOCALE_REGEX.exec(pathname);
  const locale = localeMatch ? localeMatch[1] : 'en-US';
  updateCanonical(locale, pathname, search);
  const seoKey = getSeoKeyForPath(pathname);
  console.log(`[updatePageMeta] pathname: ${pathname}, seoKey: ${seoKey}`);
  if (seoKey) updateSeoText(i18nInstance, seoKey);
};

export const getStoreBaseByLocale = (locale) => {
  switch (locale) {
    case 'en-US':
      return process.env.REACT_APP_US_STORE
    case 'en-CA':
    case 'fr-CA':
      return process.env.REACT_APP_CA_STORE
    case 'fr-FR':
    case 'nl-NL':
    case 'pt-BR':
    case 'en-US(INT)':
      return process.env.REACT_APP_INTL_STORE
    default:
      return process.env.REACT_APP_US_STORE || process.env.REACT_APP_LEGACY_APP_URL
  }
};

export const urlFormatter = ({ locale = 'en-US', page = '', query = '', hash = '' }) => {
  const normalizedLocale = locale || process.env.REACT_APP_APP_STORE_LANGUAGE || 'en-US';
  const base = getStoreBaseByLocale(normalizedLocale)
    || process.env.REACT_APP_LEGACY_APP_URL
    || process.env.REACT_APP_US_STORE
    || process.env.REACT_APP_SHELL_URL;
  if (!base) return '';
  let url = `${base.replace(/\/$/, '')}/${normalizedLocale}`;
  if (page) url += `/${page.replace(/^\//, '')}`;
  if (query) url += `?${query.replace(/^\?/, '')}`;
  if (hash) url += `#${hash.replace(/^#/, '')}`;
  return url;
};

export const getMarketplaceListingUrl = ({
  locale = 'en-US',
  solutionName,
  facetType,
  attributeId,
  page,
  apicLabel,
}) => {
  // apicLabel always wins when navigating to an overview page, regardless of attributeId
  if (apicLabel && page && page !== 'listing') {
    return urlFormatter({ locale, page: `${page}/${slugify(apicLabel)}` });
  }
  if (attributeId) {
    const query = `attr=${attributeId}`;

    if (page === 'listing') {
      return `listing?${query}#f:${facetType}=[${encodeURIComponent(solutionName || '')}]`;
    }
    return urlFormatter({ locale, page, query });
  }
  // For facetType-only case: return a relative listing path with only the facet
  // fragment (no default params) so navigateToListingPage can assemble the full
  // URL with defaults without duplicating them.
  const encodedSolution = encodeURIComponent(solutionName || '');
  const facetFragment = facetType ? `f:${facetType}=[${encodedSolution}]` : '';
  return facetFragment ? `/listing#${facetFragment}` : '/listing';
};

export function getSafeId(prefix, value) {
  return `${prefix}-${String(value).toLowerCase().replace(/[^a-z0-9]/g, '-')}`;
}

export const getStoreUrl = (locale = 'en-US', tab) => {
  const tabToPage = {
    about: 'pages/why-marketplace',
    ai_solutions: 'pages/ai',
  };
  const page = tabToPage[tab] || 'home';
  const base = getStoreBaseByLocale(locale || 'en-US');
  if (!base) return '';
  return `${(base || '').replace(/\/+$/, '')}/${locale}/${page}`;
};

export const buildLegacyLoginContinueUrl = (url, authenticated = !!window.__AUTH_STATE__?.authenticated) => {
  if (!url || !authenticated || !ABSOLUTE_HTTP_URL_REGEX.test(url)) return url;

  try {
    const requestedUrl = new URL(url);
    const legacyBase = CONFIG.LEGACY_APP_URL ? new URL(CONFIG.LEGACY_APP_URL) : null;
    if (!legacyBase || requestedUrl.origin !== legacyBase.origin) return url;

    if (/^\/login(?:\/|$)/.test(requestedUrl.pathname) || /^\/logout(?:\/|$)/.test(requestedUrl.pathname)) {
      return url;
    }

    if (isShellOwnedRoutePath(requestedUrl.pathname)) {
      return url;
    }

    const legacyBasePath = trimTrailingSlashes(legacyBase.pathname);
    let relativePathname = requestedUrl.pathname;

    if (legacyBasePath && relativePathname.startsWith(legacyBasePath)) {
      relativePathname = relativePathname.slice(legacyBasePath.length) || '/';
    }

    if (!relativePathname.startsWith('/')) {
      relativePathname = `/${relativePathname}`;
    }

    const continuePath = `${relativePathname}${requestedUrl.search}${requestedUrl.hash}`;
    return `${legacyBase.origin}/login?continue=${encodeURIComponent(continuePath)}`;
  } catch {
    return url;
  }
};



export const getCookie = (cookieName) => {
  const cookie = {};
  document.cookie.split(';').forEach(function (el) {
    const [key, value] = el.split('=');
    cookie[key.trim()] = value;
  });
  return cookie[cookieName];
};

export const getPageType = () => {
  const currentPath = window.location.pathname;
  if (currentPath === "/" || currentPath?.includes(["/home"])) {
    return APP_ROUTES?.["/home"];
  }

  if (currentPath?.includes(["/smart-search"])) {
    return APP_ROUTES["/smart-search"];
  }
}
export const getCategoriesAndFacetType = (type, solutions_facetType, industry_facetType) => {
  if (type === 'industry' || type === 'partners') {
    return {
      facetType: industry_facetType,
      pageKey: 'industry'
    };
  }
  return {
    facetType: solutions_facetType,
    pageKey: 'solutions'
  };
};

export const getTranslatedCategories = (categoriesData, type, solutionCategories = {}, industryCategories = {}) => {
  const [primary, secondary] = type === 'solutions'
    ? [solutionCategories, industryCategories]
    : [industryCategories, solutionCategories];

  const findEntry = (map, name, attributeId) =>
    Object.entries(map).find(([key, cat]) => key === name || cat?.label === name) ||
    Object.entries(map).find(([, cat]) => String(cat?.attributeId) === String(attributeId));

  return (categoriesData || []).map((category) => {
    const entry =
      findEntry(primary, category?.name, category?.attributeId) ||
      findEntry(secondary, category?.name, category?.attributeId) ||
      [];
    const [matchedKey, translation] = entry;

    return {
      label: translation?.label || category?.name,
      overviewLabel: translation?.overviewLabel || '',
      attributeId: category?.attributeId,
      hideOverview: !!translation?.hideOverview || !translation?.label,
      key: matchedKey || category?.name,
      name: category?.name,
      description: translation?.description || ''
    };
  });
};
export const SHELL_BASE_PATH = (() => {
  try {
    // Use the value from CONFIG, which is wired to REACT_APP_SHELL_URL
    const url = new URL(CONFIG.SHELL_URL || window.location.origin);
    return url.pathname.replace(/\/+$/, ''); // e.g. '/cmp'
  } catch {
    return '';
  }
})();

// Normalize a pathname to be relative to the shell base
export const toShellRelativePath = (pathname) => {
  let rel = pathname || '/';

  if (SHELL_BASE_PATH && rel.startsWith(SHELL_BASE_PATH)) {
    rel = rel.slice(SHELL_BASE_PATH.length) || '/';
  }

  // Handle '/index.html' suffixes
  if (rel === '/index.html') {
    rel = '/';
  } else if (rel.endsWith('/index.html')) {
    rel = rel.slice(0, -'/index.html'.length) || '/';
  }

  if (!rel.startsWith('/')) rel = '/' + rel;
  return rel;
}

export const stripLocalePrefix = (pathname = '/') => {
  const normalizedPath = toShellRelativePath(pathname);
  const pathWithoutLocale = normalizedPath.replace(LOCALE_PREFIX_REGEX, '') || '/';
  return pathWithoutLocale.startsWith('/') ? pathWithoutLocale : `/${pathWithoutLocale}`;
}

export const getActiveRoutes = (location, route = '') => {
  const path = stripLocalePrefix(toShellRelativePath(location.pathname));
  const isMatch = path.startsWith(`/${route}`);
  isMatch && console.log(`SPA Active Route matched: ${path} matches ${route}`);
  return isMatch;
}


export const slugify = (appName = '') =>
    appName
        .replace(/[®™©]/g, '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-');


export const handleProductClick = ({ e, product = {}, activeTab = '', navigate, tab, event, isSmartSearch = false, onCloseActiveTab }) => {
    e?.preventDefault();
    e?.stopPropagation();

    const { id: appId = '', name: appName = '' } = product || {};

    const url = appId
            ? `${CONFIG.LEGACY_APP_URL}/apps/${appId}/${slugify(appName)}${tab ? `/${tab}` : ''}`
            : ''
     sendGA4Data(event, {
            customEventName: `${events.EVENT_NAMES.HEADER_SUBMENU}_${activeTab}`,
            eventCategory: product.category,
            eventLabel: appId,
        });

    // Collapse the open header dropdown the product card was clicked from.
    if (activeTab) {
        onCloseActiveTab?.();
    }

    if (url) {
        navigate?.(url);
    }
}

export const countryNavigateUrl = ({ localeValue }) =>{
  const storeBase = getStoreBaseByLocale(localeValue);
  localeValue = localeValue?.replace(/\(.*\)/, '')?.replace(/[^a-zA-Z0-9-].*$/, '');
  const storeUrl = `${(storeBase || '').replace(/\/+$/, '')}/${localeValue}/home`;
  return storeUrl;
}